import { Alert, Button, Card, Dropdown, Form, Input, Text } from '@ui/themed';
import { t } from 'i18next';
import { useSettings } from '../../../../context/settings-context';
import { useCurrencyList } from '../../../../hooks/use-currency-list';
import { useFormProps } from '../../../../hooks/use-forms-props';
import { stringToSlug } from '../../../../utils/slug';
import { MAX_HANDLE_LEN } from '../../constants';
import { usePriceListForm } from '../../hooks/use-price-list-form';
import { AutomaticPricingForm } from './automatic-pricing-form';
import { ManualPricingForm } from './manual-pricing';

export function PriceListBuilder({
  onCancel,
  isEditing,
}: {
  onCancel: () => Promise<void> | void;
  isEditing?: boolean;
}) {
  const createOrUpdate = isEditing ? 'update' : 'create';

  const settings = useSettings();
  const form = usePriceListForm();
  const formProps = useFormProps(form);

  const currencies = useCurrencyList(settings.baseCurrency);

  return (
    <Form {...formProps}>
      <Card>
        <Card.Header headingLevel="h2">{t('price_list_builder.details.title')}</Card.Header>
        <Card.Body>
          <fieldset className="flex flex-col gap-3 sm:flex-row">
            <span className="flex-1">
              <form.Field
                name="name"
                listeners={{
                  onChange: ({ value, fieldApi }) => {
                    // Only update slug if creating new price list
                    // (Can't edit slug once created)
                    if (!isEditing) {
                      fieldApi.form.setFieldValue('handle', stringToSlug(value));
                    }
                  },
                }}
              >
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={field.handleChange}
                    error={field.state.meta.errors[0]?.message}
                    label={t('price_list_builder.details.name.label')}
                    placeholder={t('price_list_builder.details.name.placeholder')}
                  />
                )}
              </form.Field>
            </span>

            <span className="flex-1">
              <form.Field name="handle">
                {(field) => (
                  <Input
                    value={field.state.value}
                    error={field.state.meta.errors[0]?.message}
                    onChange={field.handleChange}
                    maxLength={MAX_HANDLE_LEN}
                    label={t('price_list_builder.details.handle.label')}
                    placeholder={t('price_list_builder.details.handle.placeholder')}
                    disabled={isEditing}
                  />
                )}
              </form.Field>
            </span>
          </fieldset>

          <form.Field name="currency">
            {(field) => (
              <Dropdown
                label={t('price_list_builder.details.currency.label')}
                placeholder={t('price_list_builder.details.currency.placeholder')}
                options={currencies}
                value={field.state.value}
                onChange={field.handleChange}
                error={field.state.meta.errors[0]?.message}
                disabled={isEditing}
              />
            )}
          </form.Field>

          <Text variant="secondary">
            {t('price_list_builder.details.no_changing_handle_or_currency')}
          </Text>
        </Card.Body>
      </Card>

      <AutomaticPricingForm />

      {!isEditing ? (
        <ManualPricingForm />
      ) : (
        <Card>
          <Card.Header headingLevel="h2">
            {t('price_list_builder.manual_pricing.title')}
          </Card.Header>
          <Card.Body>
            <Text variant="secondary">{t('price_list_builder.manual_pricing.description')}</Text>
            <Alert variant="info" headingLevel="h3">
              <div className="flex flex-col gap-2">
                {t('price_list_builder.manual_pricing.disabled_alert.message')}
              </div>
            </Alert>
          </Card.Body>
        </Card>
      )}

      <span className="flex justify-end gap-2">
        <Button disabled={formProps.loading} variant="secondary" onClick={onCancel}>
          {t('buttons:cancel')}
        </Button>
        <Button type="submit" disabled={!formProps.isDirty} loading={formProps.loading}>
          {t(`price_list_builder.actions.${createOrUpdate}`)}
        </Button>
      </span>
    </Form>
  );
}
